import type { MixUseKey } from "features/dt-basic-setup/single-building/model/types";
import type { SummaryMixConfigurationModalProps } from "features/dt-basic-setup/single-building/model/types";

import { Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { CustomModal } from "components/CustomModal";
import { DividerHorizontal } from "components/Divider";
import { InlineDangerMessage } from "components/InlineDangerMessage";
import { LabelLessInput } from "components/Input";
import { MIX_USE_KEYS } from "features/dt-basic-setup/single-building/model/mix";
import React from "react";
import { useTranslation } from "react-i18next";
import { percent } from "utils/textSymbols";

export const SummaryMixConfigurationModal = ({
  handleSaveMixConfiguration,
  hasInvalidMixTotal,
  mixDraft,
  mixError,
  mixModalOpen,
  mixTotal,
  setMixModalOpen,
  updateMixValue,
}: SummaryMixConfigurationModalProps) => {
  const { t } = useTranslation();
  const mixRowLabel = (key: MixUseKey) => t(`AddBuilding.summaryStep.buildingTypeLabels.${key}`);

  return (
    <CustomModal
      className=""
      height="70rem"
      left="50%"
      modalOpen={mixModalOpen}
      padding={undefined}
      right={undefined}
      setModalOpen={setMixModalOpen}
      style={undefined}
      top="50%"
      width="55rem"
    >
      <Stack className="h-full w-full flex-col items-start px-4">
        <Stack className="w-full flex-col justify-center">
          <h3 className="font-ds-headline-lg text-center">{t("AddBuilding.summaryStep.mixModal.title")}</h3>
          <p className="mt-1 text-center text-lds-foreground-muted font-ds-body-regular-xs">
            {t("AddBuilding.summaryStep.mixModal.subtitle")}
          </p>
        </Stack>
        <Stack className="mt-7 min-h-0 flex-1 flex-col overflow-auto">
          <Stack className="w-full flex-col items-start gap-1 pl-12">
            {MIX_USE_KEYS.map((key) => (
              <Stack className="w-full gap-20" key={key}>
                <Stack className="min-w-0 flex-1 py-2.5 pl-2.5">
                  <p className="text-lds-foreground-secondary font-ds-body-bold-md">
                    {t("AddBuilding.summaryStep.mixModal.rowLabel", { type: mixRowLabel(key) })}
                  </p>
                </Stack>
                <Stack className="gap-2">
                  <LabelLessInput
                    autoFocus={false}
                    className="w-28"
                    height="3.6rem"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateMixValue(key, Number(e.target.value))}
                    type="number"
                    value={mixDraft[key] ? String(mixDraft[key]) : ""}
                    width={undefined}
                  />
                  <span className="text-lds-foreground-secondary font-ds-body-regular-xs">{percent}</span>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <div className="w-full pl-12">
            <DividerHorizontal className="mt-3" />
            <Stack className="mt-3 w-full ">
              <Stack className="w-full justify-between">
                <p className="font-ds-headline-lg text-lds-foreground-primary">
                  {t("AddBuilding.summaryStep.mixModal.totalLabel")}
                </p>
                <p
                  className={`font-ds-body-regular-md ${
                    hasInvalidMixTotal ? "text-lds-purpose-status-negative-subtle" : "text-lds-foreground-primary"
                  }`}
                >
                  {mixTotal}
                  {percent}
                </p>
              </Stack>
            </Stack>
            {!!mixError && <InlineDangerMessage className="mt-3" message={mixError} />}
          </div>
        </Stack>
        <Stack className="mt-3 w-full justify-center gap-4 pb-2">
          <Button onClick={() => setMixModalOpen(false)} size="sm" variant="secondary">
            {t("general.cancel")}
          </Button>
          <Button onClick={handleSaveMixConfiguration} size="sm" variant="primary">
            {t("AddBuilding.summaryStep.mixModal.save")}
          </Button>
        </Stack>
      </Stack>
    </CustomModal>
  );
};
